import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Eye, EyeOff, X } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState('username'); // 'username' or 'phone'
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const loginPayload = {
        password: loginData.password,
      };

      if (loginType === 'username') {
        loginPayload.username = loginData.username;
      } else {
        loginPayload.phone_number = loginData.username; // Using username field for phone
      }
      
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginPayload),
      });

      let data;
      try {
        data = await response.json();
      } catch (_) {
        alert('Invalid response from server. Please try again.');
        setLoading(false);
        return;
      }

      if (response.ok) {
        const user = data.data?.user ?? data.user;
        const token = data.data?.token ?? data.token;
        if (user) {
          const mustChange = data.data?.must_change_password ?? data.must_change_password;
          try {
            localStorage.setItem('reddyannabook_user', JSON.stringify(user));
            if (token) localStorage.setItem('reddyannabook_token', token);
            if (mustChange) {
              localStorage.setItem('must_change_password', 'true');
            } else {
              localStorage.removeItem('must_change_password');
            }
          } catch (_) {}
          onLoginSuccess(user, mustChange);
          window.dispatchEvent(new CustomEvent('user-logged-in'));
          onClose();
          setLoginData({ username: '', password: '' });
        } else {
          alert('Login succeeded but no user data received.');
        }
      } else {
        alert(data.message || data.error || 'Login failed');
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = () => {
    const demoUser = {
      id: 'demo',
      display_name: 'Demo User',
      displayName: 'Demo User',
      balance: 0,
      netExposure: 0,
      availableCredit: 0,
      bonusWallet: 0,
    };
    try {
      localStorage.setItem('reddyannabook_user', JSON.stringify(demoUser));
      localStorage.removeItem('reddyannabook_token');
    } catch (_) {}
    onLoginSuccess(demoUser);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-lotus border-none p-0 overflow-hidden">
        {/* Custom close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 text-white hover:text-gray-300 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-8 text-center">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img src="/logo.png" alt="Reddy Anna Book" className="h-10 sm:h-14" style={{ objectFit: 'contain' }} />
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Username/Phone Field */}
            <div className="space-y-2">
              <label className="block text-left text-white text-sm font-medium">
                {loginType === 'username' ? 'Username' : 'Phone Number'}
              </label>
              <div className="relative">
                <Input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  className="bg-transparent border-0 border-b-2 border-yellow-400 rounded-none text-white focus:border-yellow-400 focus:ring-0 px-0 py-2"
                  style={{ color: 'white', '--tw-placeholder-opacity': 1 }}
                  placeholder={loginType === 'username' ? 'Enter username' : 'Enter phone number'}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-left text-white text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="bg-transparent border-0 border-b-2 border-yellow-400 rounded-none text-white focus:border-yellow-400 focus:ring-0 px-0 py-2 pr-10"
                  style={{ color: 'white', '--tw-placeholder-opacity': 1 }}
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                className="text-gray-300 hover:text-white text-sm underline"
                onClick={() => { onClose(); navigate('/forgot-password'); }}
              >
                Forgot Password?
              </button>
            </div>

            {/* Login Buttons */}
            <div className="space-y-3">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-gray-100 font-bold py-3 rounded-md"
              >
                {loading ? 'Logging in...' : 'LOGIN'}
              </Button>
              
              <Button
                type="button"
                onClick={handleDemoLogin}
                className="w-full bg-yellow-400 text-black hover:bg-yellow-500 font-bold py-3 rounded-md"
              >
                LOGIN WITH DEMO ID
              </Button>
            </div>
          </form>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
